import { useId, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { accountEmail, accountInitials, accountName } from '@/components/shell/AccountMenu'
import { Content, Ltr } from '@/components/ui/Bidi'
import { formatShowableBand, parseTargetBand } from '@/domain/results/band-display'
import { useAuth } from '@/features/auth/auth-context'
import type { StoredStudentIdentity } from '@/features/profile/profile-types'
import { isolateContent, t, useLanguage } from '@/lib/i18n'
import { Avatar } from './Bits'
import { GlyphTile, Icon, type GlyphName } from './glyphs'
import { LanguageSwitch } from './LanguageSwitch'
import { Popover } from './Overlay'

export interface AvatarMenuProps {
  /** A session with no `UserProfile` (the school demo) supplies its own identity: the trigger initials, the name and a detail line. */
  identity?: { name: string; detail?: string; initials: string }
  /** Replaces the four student links; an empty list drops the links block. Omit for the student menu. */
  links?: ReadonlyArray<{ to: string; label: string; glyph: GlyphName }>
  /** Replaces the student sign-out (`useAuth().logout`). */
  onSignOut?: () => void
  /** Extra rows under the links (the staff Motion setting). */
  children?: ReactNode
}

/**
 * The avatar menu (APP-DESIGN-DIRECTION 4.1): a 320px card under the avatar on desktop, a bottom sheet on a phone. A header
 * with the avatar, name and e-mail; the target band row; Profile, Achievements, Leaderboard and Settings as rows with glyph
 * tiles; the language as an inline segmented control written in each language; Sign out last.
 *
 * It keeps the disclosure contract of `components/shell/AccountMenu.tsx` (that file's helpers are reused, its behaviour is
 * unchanged): `aria-expanded` on the trigger, a labelled region, ordinary Tab order, Escape closes and returns focus to the
 * trigger, an outside press closes. Not a `role="menu"`: it promises no arrow-key roving.
 */
export function AvatarMenu({ identity, links, onSignOut, children }: AvatarMenuProps = {}) {
  useLanguage()
  const { profile, logout } = useAuth()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const [open, setOpen] = useState(false)

  // Unreachable behind RequireAuth, which redirects on a null profile. Guarded so there is never a menu with no account.
  if (!profile && !identity) return null

  const name = identity ? identity.name : accountName(profile!) || t('Your account')
  const email = identity ? null : accountEmail(profile!)
  const initials = identity ? identity.initials : accountInitials(profile!)
  const target = identity ? null : formatShowableBand(parseTargetBand((profile as StoredStudentIdentity | null)?.target))
  const close = () => setOpen(false)
  const rows = links ?? [
    { to: '/profile', label: t('Profile'), glyph: 'profile' as const },
    { to: '/achievements', label: t('Achievements'), glyph: 'achievement' as const },
    { to: '/leaderboard', label: t('Leaderboard'), glyph: 'leaderboard' as const },
    { to: '/settings', label: t('Settings'), glyph: 'settings' as const },
  ]

  return (
    <div className="r5-avatar">
      <button
        ref={triggerRef}
        type="button"
        className="r5-avatar__trigger r5-tap"
        // The initials are not a name anyone can say, so the accessible name carries the full one (WCAG 2.5.3).
        aria-label={t('Account menu for {0}', { 0: isolateContent(name) })}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true"><Content>{initials}</Content></span>
      </button>

      <Popover open={open} onClose={close} anchorRef={triggerRef} id={panelId} label={t('Account')} width={320} className="r5-avatar__panel">
        <header className="r5-avatar__identity">
          <Avatar initials={initials} size={40} self />
          <div className="r5-avatar__who">
            <p className="r5-avatar__name"><Content>{name}</Content></p>
            {email && <p className="r5-avatar__email"><Ltr>{email}</Ltr></p>}
            {identity?.detail && <p className="r5-avatar__email">{identity.detail}</p>}
          </div>
        </header>
        {!identity && (
          <Link className="r5-avatar__target" to="/plan" onClick={close}>
            <span>{target ? t('Target band') : t('Set your target')}</span>
            {target ? <strong><bdi className="num">{target}</bdi></strong> : <Icon name="chevron-end" size={16} />}
          </Link>
        )}
        {rows.length > 0 && (
          <div className="r5-avatar__links">
            {rows.map(({ to, label, glyph }) => (
              <Link key={to} className="r5-avatar__item" to={to} onClick={close}>
                <GlyphTile name={glyph} />
                <span>{label}</span>
                <Icon name="chevron-end" size={16} className="r5-avatar__chev" />
              </Link>
            ))}
          </div>
        )}
        {children}
        <div className="r5-avatar__lang">
          <span className="r5-label r5-label--ink" aria-hidden="true">{t('Language')}</span>
          <LanguageSwitch variant="menu" />
        </div>
        <div className="r5-avatar__out">
          <button type="button" className="r5-avatar__item" onClick={() => { if (onSignOut) onSignOut(); else void logout() }}>
            <Icon name="sign-out" />
            <span>{t('Sign out')}</span>
          </button>
        </div>
      </Popover>
    </div>
  )
}
